// Client-side checks for POST /AdminUtility/v0.1/upload-file.
// The backend is the source of truth; these only catch problems before a request is sent.

export const ALLOWED_EXTENSIONS = ["xlsx", "xls", "xlsb", "csv", "htm", "html", "pdf", "txt"]
export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024 // 50 MB

export const getFileExtension = (fileName = "") => {
	const lastDot = fileName.lastIndexOf(".")
	return lastDot === -1 ? "" : fileName.slice(lastDot + 1).toLowerCase()
}

// Returns an error message, or null when the file can be sent
export const validateFile = (file) => {
	if (!ALLOWED_EXTENSIONS.includes(getFileExtension(file.name))) {
		return `${file.name} is not a supported file type. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`
	}
	if (file.size > MAX_FILE_SIZE_BYTES) {
		return `${file.name} is larger than 50 MB`
	}
	return null
}

// Servier needs statement_type, kennect_month and kennect_year with every file
export const isServierClient = (client) => client?.env === "servier"

export const STATEMENT_TYPES = ["Full Month", "Mid Month"]

export const MONTH_OPTIONS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
].map((label, idx) => ({ value: idx + 1, label }))

export const SERVIER_FIELD_LABELS = {
	statementType: "Statement type",
	kennectMonth: "Month",
	kennectYear: "Financial year",
}

// The SSE month runs from the 25th to the 24th: 25 Aug is August, 24 Aug is still July
export const getSseMonth = (today = new Date()) => {
	let month = today.getMonth() + 1
	let year = today.getFullYear()
	if (today.getDate() < 25) {
		month -= 1
		if (month === 0) {
			month = 12
			year -= 1
		}
	}
	return { month, year }
}

// Financial year runs April to March, e.g. September 2026 -> "2026-2027", March 2027 -> "2026-2027"
export const getFinancialYear = (month, year) => {
	const startYear = month >= 4 ? year : year - 1
	return `${startYear}-${startYear + 1}`
}

// The financial year of the current SSE month and the 3 before it, newest first
export const getFinancialYearOptions = (today = new Date(), count = 4) => {
	const { month, year } = getSseMonth(today)
	const currentStartYear = Number(getFinancialYear(month, year).slice(0, 4))
	return Array.from({ length: count }, (_, idx) => {
		const startYear = currentStartYear - idx
		return `${startYear}-${startYear + 1}`
	})
}

// Month and year default to the current SSE month; statement type is always chosen by the user
export const getDefaultServierFields = (today = new Date()) => {
	const { month, year } = getSseMonth(today)
	return {
		statementType: null,
		kennectMonth: month,
		kennectYear: getFinancialYear(month, year),
	}
}

const isValidKennectYear = (value) => {
	const match = /^(\d{4})-(\d{4})$/.exec(value || "")
	return Boolean(match) && Number(match[2]) === Number(match[1]) + 1
}

// Returns the keys of the Servier fields that are missing or invalid on a row
export const getInvalidServierFields = (row) => {
	const invalid = []
	if (!STATEMENT_TYPES.includes(row.statementType)) invalid.push("statementType")
	if (!Number.isInteger(row.kennectMonth) || row.kennectMonth < 1 || row.kennectMonth > 12) {
		invalid.push("kennectMonth")
	}
	if (!isValidKennectYear(row.kennectYear)) invalid.push("kennectYear")
	return invalid
}

export const appendServierFields = (formData, row) => {
	formData.append("statement_type", row.statementType)
	formData.append("kennect_month", String(row.kennectMonth))
	formData.append("kennect_year", row.kennectYear)
}

// Message for a failed request. The backend's message wins whenever it sent one.
export const getUploadErrorMessage = (error) => {
	if (error?.response?.data?.message) return error.response.data.message
	if (error?.response?.status === 413) return "File is larger than 50 MB"
	if (error?.request && !error?.response) return "Network error: could not reach the server"
	return error?.message || "Unknown error"
}
